import React, { useState, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { useOctave } from '../../contexts/OctaveContext';
import { usePython } from '../../contexts/PythonContext';
import { matlabLanguageDefinition, matlabLanguageConfiguration, matlabCompletionProvider } from '../CodeEditor/languages/matlab';
import { activityApi } from '../../utils/api/DocumentEditorApi/ActivityApi';

/**
 * Single-Language Code Runner
 *
 * Encapsulates one Monaco editor + runtime (Octave or Python) + output panel.
 * Used directly by the legacy single-language CodeSnippet shell, and rendered
 * once per tab when CodeSnippet is given a multi-language `tabs` array.
 */
const SingleCodeRunner = ({
  language: languageProp,
  initialCode: initialCodeProp = '',
  isStudent = false,
  snippetId = null, // Unique ID for this snippet within the document
  documentId = null, // Document ID for activity logging
  classContext = null, // Class context for API calls
  readOnly = false,
  // When true, drop the outer border/rounded/margin/shadow so this runner
  // can sit inside an enclosing container (e.g. the multi-tab shell).
  embedded = false
}) => {
  // Normalize language (default to matlab to preserve previous behavior)
  const language = languageProp || 'matlab';

  // Initial code comes directly as a prop now
  const initialCode = initialCodeProp || '';

  // Local state for student edits (doesn't affect saved document)
  const [localCode, setLocalCode] = useState(initialCode);
  const [output, setOutput] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionTime, setExecutionTime] = useState(null);
  const [showOutput, setShowOutput] = useState(false);
  const editorRef = useRef(null);

  const { isReady: octaveReady, execute: executeOctave, cancelExecution: cancelOctave } = useOctave();
  const { isReady: pythonReady, execute: executePython, cancelExecution: cancelPython } = usePython();

  // Everyone can edit code snippets - use local code for all users
  const displayCode = localCode;

  const handleEditorMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // Register MATLAB language support if not already registered
    if (!monaco.languages.getLanguages().some(lang => lang.id === 'matlab')) {
      monaco.languages.register({ id: 'matlab' });
      monaco.languages.setLanguageConfiguration('matlab', matlabLanguageConfiguration);
      monaco.languages.setMonarchTokensProvider('matlab', matlabLanguageDefinition);
      monaco.languages.registerCompletionItemProvider('matlab', {
        ...matlabCompletionProvider,
        provideCompletionItems: (model, position) =>
          matlabCompletionProvider.provideCompletionItems(model, position, monaco)
      });
    }

    // Suppress ResizeObserver errors - this is a known browser quirk
    // Monaco can throw these during container resize animations
    const handleError = (event) => {
      if (event.message?.includes('ResizeObserver loop')) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return true;
      }
    };

    window.addEventListener('error', handleError, true);

    // Store the cleanup function
    editor._resizeErrorCleanup = () => {
      window.removeEventListener('error', handleError, true);
    };
  }, []);

  const handleCodeChange = (value) => {
    // Everyone can edit locally (doesn't save to document for anyone in preview mode)
    setLocalCode(value || '');
  };

  const handleRunCode = async () => {
    const codeToRun = localCode;

    if (!codeToRun.trim()) {
      setOutput({
        type: 'error',
        message: 'No code to execute',
        timestamp: new Date().toISOString()
      });
      setShowOutput(true);
      return;
    }

    // Check if runtime is ready based on language
    const isPython = language === 'python';
    const isMatlab = language === 'matlab';

    if (isPython && !pythonReady) {
      setOutput({
        type: 'error',
        message: 'Python is still loading... Please wait.',
        timestamp: new Date().toISOString()
      });
      setShowOutput(true);
      return;
    }

    if (isMatlab && !octaveReady) {
      setOutput({
        type: 'error',
        message: 'Octave is still loading... Please wait.',
        timestamp: new Date().toISOString()
      });
      setShowOutput(true);
      return;
    }

    if (!isPython && !isMatlab) {
      setOutput({
        type: 'error',
        message: `Unsupported language: ${language}. Only 'matlab' and 'python' are supported.`,
        timestamp: new Date().toISOString()
      });
      setShowOutput(true);
      return;
    }

    // Clear previous output and artifacts to prevent caching
    setOutput(null);
    setExecutionTime(null);
    setIsExecuting(true);
    setShowOutput(true);

    let stdout = '';
    let stderr = '';
    let artifacts = [];

    // Initialize output with "executing" state
    setOutput({
      type: 'executing',
      message: '',
      timestamp: new Date().toISOString(),
      artifacts: []
    });

    try {
      // Select execution function based on language
      const executeFunction = isPython ? executePython : executeOctave;

      executeFunction(codeToRun, {
        onStdout: (data) => {
          stdout += data + '\n';
          // Update output gradually as stdout comes in
          setOutput(prev => ({
            ...prev,
            type: 'executing',
            message: stdout
          }));
        },
        onStderr: (data) => {
          stderr += data + '\n';
          // Update output gradually as stderr comes in
          setOutput(prev => ({
            ...prev,
            type: 'executing',
            message: stderr
          }));
        },
        onArtifacts: (data) => {
          // Merge with existing streaming plots
          artifacts = [...artifacts, ...data];
        },
        onPlotStream: (plotData) => {
          // Handle streaming plots - append them incrementally (Python only)
          artifacts = [...artifacts, ...plotData];
          setOutput(prev => ({
            ...prev,
            artifacts: [...(prev.artifacts || []), ...plotData]
          }));
        },
        onComplete: (result, executionTimeMs) => {
          // Use execution time from worker (measured at actual execution)
          const execTime = executionTimeMs
            ? (executionTimeMs / 1000).toFixed(2) + 's'
            : '0.00s';
          setExecutionTime(execTime);

          // Clean up stdout based on language
          let cleanOutput = stdout || '';

          if (isMatlab) {
            // Clean up Octave-specific output
            cleanOutput = cleanOutput
              .split('\n')
              .filter(line => {
                // Filter out Octave automatic "ans" output
                if (line.match(/^ans\s*=\s*(\[\]\(\d+x\d+\)|\d+)\s*$/)) return false;
                // Filter out plot metadata messages
                if (line.match(/^(Plot data saved to|Title set:|X-label set:|Y-label set:|Z-label set:)/)) return false;
                return true;
              })
              .join('\n')
              .trim();
          } else if (isPython) {
            // Python output is generally clean, just trim
            cleanOutput = cleanOutput.trim();
          }

          // Determine if there's an error based on exit code
          // Exit code 0 means success, non-zero means failure
          // Only check stderr if exit code indicates failure, as stderr may contain warnings
          const exitCode = result?.exitCode ?? 0;
          const hasError = exitCode !== 0;
          
          // Debug logging to help diagnose issues
          if (process.env.NODE_ENV === 'development') {
            console.log('[CodeSnippet] Execution result:', {
              exitCode,
              hasError,
              stdoutLength: cleanOutput.length,
              stderrLength: stderr.length,
              result
            });
          }

          setOutput({
            type: hasError ? 'error' : 'success',
            message: hasError ? (stderr || cleanOutput || 'Execution failed') : cleanOutput,
            timestamp: new Date().toISOString(),
            artifacts: artifacts
          });
          setIsExecuting(false);

          // Log activity for students
          if (isStudent && documentId && classContext && snippetId) {
            try {
              const executionTimeSeconds = executionTimeMs ? executionTimeMs / 1000 : 0;
              activityApi.logActivity(
                documentId,
                'code_run',
                `Ran ${language} code snippet`,
                {
                  metadata: {
                    snippetId: snippetId,
                    language: language,
                    executionTime: executionTimeSeconds,
                    codeLength: codeToRun.length,
                    outputLength: cleanOutput.length,
                    hasError: hasError,
                    exitCode: exitCode
                  }
                },
                classContext
              ).catch(err => {
                console.warn('Failed to log code run activity:', err);
              });
            } catch (err) {
              console.warn('Failed to log code run activity:', err);
            }
          }
        },
        onError: (error, executionTimeMs) => {
          // Use execution time from worker (measured at actual execution)
          const execTime = executionTimeMs
            ? (executionTimeMs / 1000).toFixed(2) + 's'
            : '0.00s';
          setExecutionTime(execTime);

          setOutput({
            type: 'error',
            message: `Execution failed: ${error}`,
            timestamp: new Date().toISOString()
          });
          setIsExecuting(false);

          // Log activity for students even on error
          if (isStudent && documentId && classContext && snippetId) {
            try {
              const executionTimeSeconds = executionTimeMs ? executionTimeMs / 1000 : 0;
              activityApi.logActivity(
                documentId,
                'code_run',
                `Ran ${language} code snippet (error)`,
                {
                  metadata: {
                    snippetId: snippetId,
                    language: language,
                    executionTime: executionTimeSeconds,
                    codeLength: codeToRun.length,
                    hasError: true,
                    error: error.toString()
                  }
                },
                classContext
              ).catch(err => {
                console.warn('Failed to log code run activity:', err);
              });
            } catch (err) {
              console.warn('Failed to log code run activity:', err);
            }
          }
        }
      });
    } catch (error) {
      setOutput({
        type: 'error',
        message: `Execution failed: ${error.message}`,
        timestamp: new Date().toISOString()
      });
      setIsExecuting(false);
    }
  };

  const handleStopExecution = () => {
    const isPython = language === 'python';
    const isMatlab = language === 'matlab';

    // Immediately update local state
    setIsExecuting(false);
    setOutput({
      type: 'error',
      message: 'Execution cancelled by user. Worker is restarting...',
      timestamp: new Date().toISOString()
    });

    // Cancel the execution (this will terminate and restart the worker)
    if (isPython && cancelPython) {
      cancelPython();
    } else if (isMatlab && cancelOctave) {
      cancelOctave();
    }
  };

  const handleResetCode = () => {
    // Anyone can reset to the original code
    setLocalCode(initialCode);
    setOutput(null);
    setShowOutput(false);
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (editorRef.current?._resizeErrorCleanup) {
        editorRef.current._resizeErrorCleanup();
      }
    };
  }, []);

  // Helper function to download SVG as PDF (matching ERD editor style)
  const downloadAsPDF = (svgElement, filename) => {
    if (!svgElement) return;

    try {
      const today = new Date();
      const svgData = new XMLSerializer().serializeToString(svgElement);

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${filename}</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
              background: white;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .diagram-container {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              min-height: 400px;
              user-select: none;
            }
            .diagram-container svg {
              max-width: 100%;
              height: auto;
              user-select: none;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              font-size: 12px;
              color: #666;
              border-top: 1px solid #ccc;
              padding-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>MATLAB Plot</h1>
            <p>Date: ${today.toLocaleDateString()}</p>
          </div>
          <div class="diagram-container">
            ${svgData}
          </div>
          <div class="footer">
            <p>Generated on ${today.toLocaleString()}</p>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Render 3D plots helper (same as OutputPane)
  const render3DPlot = (plot, jsonData, idx) => {
    const width = 500;
    const height = 400;
    const padding = 50;

    const projectIsometric = (x, y, z, xMin, xMax, yMin, yMax, zMin, zMax) => {
      const xNorm = (x - xMin) / (xMax - xMin || 1);
      const yNorm = (y - yMin) / (yMax - yMin || 1);
      const zNorm = (z - zMin) / (zMax - zMin || 1);

      const plotWidth = width - 2 * padding;
      const plotHeight = height - 2 * padding;

      const angle = Math.PI / 6;
      const px = padding + plotWidth * 0.5 + (xNorm - yNorm) * Math.cos(angle) * plotWidth * 0.4;
      const py = height - padding - plotHeight * 0.2 - zNorm * plotHeight * 0.6 - (xNorm + yNorm) * Math.sin(angle) * plotHeight * 0.3;

      return { px, py };
    };

    if (jsonData.type === 'plot3d') {
      const { x, y, z } = jsonData;
      const xMin = Math.min(...x), xMax = Math.max(...x);
      const yMin = Math.min(...y), yMax = Math.max(...y);
      const zMin = Math.min(...z), zMax = Math.max(...z);

      const pathData = x.map((xVal, i) => {
        const { px, py } = projectIsometric(xVal, y[i], z[i], xMin, xMax, yMin, yMax, zMin, zMax);
        return `${i === 0 ? 'M' : 'L'} ${px} ${py}`;
      }).join(' ');

      return (
        <div key={idx} className="bg-white border rounded p-3 mt-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600">3D Plot</span>
            <button
              onClick={() => {
                const svgElement = document.getElementById(`snippet-plot-3d-svg-${idx}`);
                if (svgElement) {
                  downloadAsPDF(svgElement, `plot_${idx + 1}_3d`);
                }
              }}
              className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Download PDF
            </button>
          </div>
          <svg id={`snippet-plot-3d-svg-${idx}`} viewBox={`0 0 ${width} ${height}`} className="w-full h-auto max-h-96" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none', cursor: 'grab' }}>
            <path d={pathData} fill="none" stroke="#3b82f6" strokeWidth="2" />
            {x.map((xVal, i) => {
              const { px, py } = projectIsometric(xVal, y[i], z[i], xMin, xMax, yMin, yMax, zMin, zMax);
              return <circle key={i} cx={px} cy={py} r="2" fill="#3b82f6" />;
            })}
          </svg>
        </div>
      );
    }

    return null;
  };

  // Render 2D plots
  const render2DPlot = (plot, jsonData, idx) => {
    const { x, y } = jsonData;
    const width = 500;
    const height = 300;
    const padding = 40;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    const xMin = Math.min(...x), xMax = Math.max(...x);
    const yMin = Math.min(...y), yMax = Math.max(...y);

    const xScale = (val) => padding + ((val - xMin) / (xMax - xMin)) * plotWidth;
    const yScale = (val) => height - padding - ((val - yMin) / (yMax - yMin)) * plotHeight;

    const pathData = x.map((xVal, i) => {
      return `${i === 0 ? 'M' : 'L'} ${xScale(xVal)} ${yScale(y[i])}`;
    }).join(' ');

    return (
      <div key={idx} className="bg-white border rounded p-3 mt-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">Plot {idx + 1}</span>
          <button
            onClick={() => {
              const svgElement = document.getElementById(`snippet-plot-2d-svg-${idx}`);
              if (svgElement) {
                downloadAsPDF(svgElement, `plot_${idx + 1}`);
              }
            }}
            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Download PDF
          </button>
        </div>
        <svg id={`snippet-plot-2d-svg-${idx}`} viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none', cursor: 'grab' }}>
          <rect x={padding} y={padding} width={plotWidth} height={plotHeight} fill="#f9fafb" />
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#374151" strokeWidth="2" />
          <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#374151" strokeWidth="2" />
          <path d={pathData} fill="none" stroke="#3b82f6" strokeWidth="2" />
          {x.map((xVal, i) => (
            <circle key={i} cx={xScale(xVal)} cy={yScale(y[i])} r="2" fill="#3b82f6" />
          ))}
        </svg>
      </div>
    );
  };

  // Outer container classes depend on whether this runner is embedded inside
  // a parent container (e.g. the multi-tab shell). Embedded runners shed the
  // border/rounded/margin/shadow so they fit cleanly under the tab strip.
  const outerClassName = embedded
    ? 'bg-white'
    : 'my-4 border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm';

  // Read-only mode: show static code without editor or execution
  if (readOnly) {
    return (
      <div className={outerClassName}>
        <div className="bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">{language.toUpperCase()} Code</span>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Read Only</span>
        </div>
        <pre className="bg-gray-900 text-gray-100 p-4 font-mono text-sm overflow-auto" style={{ maxHeight: '400px' }}>
          <code>{initialCode}</code>
        </pre>
      </div>
    );
  }

  return (
    <div className={outerClassName}>
      {/* Header */}
      <div className="bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">
            {language.toUpperCase()} Code
          </span>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
            Interactive
          </span>
          {((language === 'python' && !pythonReady) || (language === 'matlab' && !octaveReady)) && (
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded animate-pulse">
              {isExecuting ? 'Running...' : 'Initializing...'}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {localCode !== initialCode && (
            <button
              onClick={handleResetCode}
              className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
              title="Reset to original code"
            >
              Reset
            </button>
          )}
          {isExecuting ? (
            <button
              onClick={handleStopExecution}
              className="px-4 py-1 text-sm font-medium rounded transition-colors bg-red-600 hover:bg-red-700 text-white"
              title="Stop execution"
            >
              Stop
            </button>
          ) : (
            <button
              onClick={handleRunCode}
              disabled={language === 'python' ? !pythonReady : !octaveReady}
              className={`px-4 py-1 text-sm font-medium rounded transition-colors ${
                (language === 'python' ? !pythonReady : !octaveReady)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              Run
            </button>
          )}
        </div>
      </div>

      {/* Monaco Editor */}
      <div className="border-b border-gray-200" style={{ height: '250px' }}>
        <Editor
          height="100%"
          language={language}
          value={displayCode}
          onChange={handleCodeChange}
          onMount={handleEditorMount}
          theme="vs-light"
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            readOnly: false, // Everyone can edit code snippets
            wordWrap: 'on',
            padding: { top: 8, bottom: 8 }
          }}
        />
      </div>

      {/* Output Section */}
      {showOutput && (
        <div className="bg-gray-50 border-t border-gray-200">
          <div className="px-4 py-2 flex items-center justify-between border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Output</span>
              {executionTime && (
                <span className="text-xs text-gray-500">({executionTime})</span>
              )}
            </div>
            <button
              onClick={() => setShowOutput(false)}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              ✕
            </button>
          </div>

          <div className="p-4 max-h-96 overflow-auto">
            {output ? (
              <>
                {/* Console Output */}
                <div className={`bg-gray-900 text-sm p-3 rounded font-mono whitespace-pre-wrap ${
                  output.type === 'error' ? 'text-red-400' :
                  output.type === 'executing' ? 'text-yellow-400' :
                  'text-green-400'
                }`}>
                  {output.message || (output.type === 'executing' ? 'Executing code...' : 'No output yet')}
                  {output.type === 'executing' && <span className="animate-pulse"> ▊</span>}
                </div>

                {/* Plot Artifacts (excluding metadata files) */}
                {output.artifacts && output.artifacts.filter(a => a.type === 'plot').length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      Generated Plots ({output.artifacts.filter(a => a.type === 'plot').length})
                    </div>
                    <div className="space-y-2">
                      {output.artifacts.filter(a => a.type === 'plot').map((plot, idx) => {
                        if (plot.mime_type === 'application/json') {
                          try {
                            const jsonData = JSON.parse(new TextDecoder().decode(plot.data));
                            if (jsonData.type === 'plot3d' || jsonData.type === 'surf') {
                              return render3DPlot(plot, jsonData, idx);
                            }
                            if (jsonData.type === 'heatmap') {
                              // Render heatmap
                              const { rows, cols, data, colormap: cmap, title: heatmapTitle } = jsonData;
                              const width = 500;
                              const height = 400;
                              const padding = 50;
                              const cellWidth = (width - 2 * padding) / cols;
                              const cellHeight = (height - 2 * padding) / rows;

                              const minVal = Math.min(...data);
                              const maxVal = Math.max(...data);
                              const range = maxVal - minVal || 1;

                              const getColor = (value) => {
                                const normalized = (value - minVal) / range;
                                if (cmap === 'hot') {
                                  if (normalized < 0.33) {
                                    const t = normalized / 0.33;
                                    return `rgb(${Math.round(255 * t)}, 0, 0)`;
                                  } else if (normalized < 0.66) {
                                    const t = (normalized - 0.33) / 0.33;
                                    return `rgb(255, ${Math.round(255 * t)}, 0)`;
                                  } else {
                                    const t = (normalized - 0.66) / 0.34;
                                    return `rgb(255, 255, ${Math.round(255 * t)})`;
                                  }
                                } else {
                                  const r = Math.round(255 * normalized);
                                  const b = Math.round(255 * (1 - normalized));
                                  return `rgb(${r}, 0, ${b})`;
                                }
                              };

                              return (
                                <div key={idx} className="bg-white border rounded p-3 mt-2">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-600">Heatmap {idx + 1}</span>
                                    <button
                                      onClick={() => {
                                        const svgElement = document.getElementById(`snippet-heatmap-svg-${idx}`);
                                        if (svgElement) {
                                          downloadAsPDF(svgElement, `heatmap_${idx + 1}`);
                                        }
                                      }}
                                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                    >
                                      Download PDF
                                    </button>
                                  </div>
                                  <svg id={`snippet-heatmap-svg-${idx}`} viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" style={{ userSelect: 'none' }}>
                                    {heatmapTitle && (
                                      <text x={width / 2} y={25} textAnchor="middle" fontSize="14" fontWeight="bold" fill="#1f2937">
                                        {heatmapTitle}
                                      </text>
                                    )}
                                    {data.map((value, i) => {
                                      const row = Math.floor(i / cols);
                                      const col = i % cols;
                                      const x = padding + col * cellWidth;
                                      const y = padding + row * cellHeight;
                                      return (
                                        <rect
                                          key={i}
                                          x={x}
                                          y={y}
                                          width={cellWidth}
                                          height={cellHeight}
                                          fill={getColor(value)}
                                          stroke="#fff"
                                          strokeWidth="0.5"
                                        />
                                      );
                                    })}
                                    <g transform={`translate(${width - padding + 10}, ${padding})`}>
                                      {Array.from({ length: 50 }, (_, i) => {
                                        const value = minVal + (i / 50) * range;
                                        const y = (height - 2 * padding) * (1 - i / 50);
                                        return (
                                          <rect
                                            key={i}
                                            x={0}
                                            y={y}
                                            width={15}
                                            height={(height - 2 * padding) / 50}
                                            fill={getColor(value)}
                                          />
                                        );
                                      })}
                                      <text x={20} y={0} fontSize="9" fill="#374151">{maxVal.toFixed(1)}</text>
                                      <text x={20} y={height - 2 * padding} fontSize="9" fill="#374151">{minVal.toFixed(1)}</text>
                                    </g>
                                  </svg>
                                </div>
                              );
                            }
                            return render2DPlot(plot, jsonData, idx);
                          } catch (err) {
                            return (
                              <div key={idx} className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
                                Failed to render plot: {plot.filename}
                              </div>
                            );
                          }
                        } else {
                          // SVG plots from Python (matplotlib)
                          const isSvg = plot.mime_type === 'image/svg+xml';

                          if (isSvg) {
                            return (
                              <div key={idx} className="bg-white border rounded p-3 mt-2">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-gray-600">Plot {idx + 1}</span>
                                  <button
                                    onClick={() => {
                                      // Convert Uint8Array to blob and download
                                      const blob = new Blob([plot.data], { type: plot.mime_type });
                                      const url = URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = plot.filename;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      URL.revokeObjectURL(url);
                                    }}
                                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                  >
                                    Download
                                  </button>
                                </div>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: new TextDecoder().decode(plot.data)
                                  }}
                                  className="max-w-full bg-white"
                                  style={{ maxHeight: '400px', overflow: 'auto' }}
                                />
                              </div>
                            );
                          }
                        }
                        return null;
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-gray-500 py-4">
                No output yet
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * CodeSnippet — public shell.
 *
 * Two ways to invoke:
 *   1) Multi-language tabs: pass a `tabs` array of `{ language, code }`.
 *      Each tab is rendered as its own SingleCodeRunner; inactive tabs are
 *      kept mounted but hidden via `display: none` so Monaco editor state and
 *      output persist across tab switches.
 *   2) Legacy single-block: pass `className` (e.g. "language-matlab:runnable")
 *      and `children` containing the code text. The shell parses these and
 *      renders a single SingleCodeRunner with no tab strip.
 */
const CodeSnippet = ({
  tabs = null,
  // Legacy single-block props (used when `tabs` is not supplied)
  children,
  className = '',
  // Common props forwarded to every SingleCodeRunner
  isStudent = false,
  snippetId = null,
  documentId = null,
  classContext = null,
  readOnly = false
}) => {
  // Build the effective tab list. If a `tabs` array is provided we use it
  // directly; otherwise we derive a single-entry list from the legacy
  // className/children pair so existing call sites keep working unchanged.
  const effectiveTabs = React.useMemo(() => {
    if (Array.isArray(tabs) && tabs.length > 0) {
      return tabs.map(t => ({
        language: (t.language || 'matlab').toLowerCase(),
        code: typeof t.code === 'string' ? t.code : ''
      }));
    }

    const match = /language-(\w+):runnable/.exec(className || '');
    const language = match ? match[1] : 'matlab';
    const code = typeof children === 'string'
      ? children
      : (children?.props?.children || '');
    return [{ language, code }];
  }, [tabs, className, children]);

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Guard the active index in case the tab list shrinks (e.g. content reload)
  const safeActiveIndex = Math.min(activeTabIndex, effectiveTabs.length - 1);

  // Single-tab fast path — render the runner directly with no tab strip so
  // existing single-language snippets look exactly the way they did before.
  if (effectiveTabs.length === 1) {
    const only = effectiveTabs[0];
    return (
      <SingleCodeRunner
        language={only.language}
        initialCode={only.code}
        isStudent={isStudent}
        snippetId={snippetId}
        documentId={documentId}
        classContext={classContext}
        readOnly={readOnly}
      />
    );
  }

  // Multi-tab layout
  return (
    <div className="my-4 border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">
      {/* Tab Strip */}
      <div className="flex bg-gray-50 border-b border-gray-300" role="tablist">
        {effectiveTabs.map((t, idx) => {
          const isActive = idx === safeActiveIndex;
          return (
            <button
              key={`${t.language}-${idx}`}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTabIndex(idx)}
              className={`px-4 py-2 text-sm font-medium border-r border-gray-300 transition-colors ${
                isActive
                  ? 'bg-white text-gray-800 border-b-2 border-b-blue-600 -mb-px'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={`Switch to ${t.language.toUpperCase()}`}
            >
              {t.language.toUpperCase()}
            </button>
          );
        })}
      </div>

      {/* Mount all runners; hide inactive ones so per-tab state is preserved
          across tab switches (Monaco editor instance, output, run state). */}
      {effectiveTabs.map((t, idx) => {
        const isActive = idx === safeActiveIndex;
        // Per-tab snippet id keeps activity logs distinguishable per language
        const tabSnippetId = snippetId
          ? `${snippetId}:${t.language}`
          : null;
        return (
          <div
            key={`${t.language}-${idx}`}
            role="tabpanel"
            style={{ display: isActive ? 'block' : 'none' }}
          >
            <SingleCodeRunner
              language={t.language}
              initialCode={t.code}
              isStudent={isStudent}
              snippetId={tabSnippetId}
              documentId={documentId}
              classContext={classContext}
              readOnly={readOnly}
              embedded
            />
          </div>
        );
      })}
    </div>
  );
};

export default CodeSnippet;
